import React, { useState, useRef, useEffect } from 'react';
import { Database, Download, Upload, AlertTriangle, CheckCircle2, RefreshCw, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SCOPE_OPTIONS = [
  { value: 'all', label: 'Full Backup (All Data)', desc: 'Everything: Simulation + Planning + Warehouse + Users' },
  { value: 'simulation', label: 'Production Simulation', desc: 'Personnel, stations, projects, operations, prep tables, lines, shifts' },
  { value: 'planning', label: 'Production Planning', desc: 'Planning projects, production plans, MDP demands, components' },
  { value: 'warehouse', label: 'Warehouse / Sage', desc: 'Sage components, material mappings, products' },
];

export default function Backup() {
  const [scope, setScope] = useState('all');
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);
  const [dbInfo, setDbInfo] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/backup/info`).then(r => setDbInfo(r.data)).catch(() => {});
  }, [restoreResult]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/api/backup/export?scope=${scope}`);
      const data = res.data;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '').slice(0, 15);
      a.href = url;
      a.download = `kiel_${scope}_backup_${ts}.json`;
      a.click();
      URL.revokeObjectURL(url);

      const totalDocs = Object.values(data.collections || {}).reduce((s, arr) => s + (arr?.length || 0), 0);
      setLastBackup({ date: new Date().toLocaleString(), totalDocs, collections: Object.keys(data.collections || {}).length, scope });
      toast.success(`Backup downloaded: ${a.download}`);
    } catch (err) {
      toast.error('Failed to download backup');
    }
    setDownloading(false);
  };

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!window.confirm('This will import the backup data using safe merge.\nExisting records with matching IDs will be updated.\nNew records will be inserted.\nNo data will be deleted.\n\nProceed?')) {
      if (fileRef.current) fileRef.current.value = '';
      return;
    }

    setRestoring(true);
    setRestoreResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/api/backup/import`, formData);
      setRestoreResult(res.data);
      toast.success('Backup imported successfully! Refresh to see changes.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import backup');
    }
    setRestoring(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const totalDocs = dbInfo ? Object.values(dbInfo).reduce((sum, group) => sum + Object.values(group).reduce((s, c) => s + c, 0), 0) : 0;

  return (
    <div className="space-y-6" data-testid="backup-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">Database Backup & Restore</h1>
        <p className="text-sm text-slate-500 mt-0.5">Export or import your database safely</p>
      </div>

      {/* DB Overview */}
      {dbInfo && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-4 h-4 text-blue-500" />
            <h2 className="text-sm font-bold text-slate-800">Database Overview ({totalDocs} total documents)</h2>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {Object.entries(dbInfo).map(([scopeName, cols]) => {
              const count = Object.values(cols).reduce((s, c) => s + c, 0);
              const colCount = Object.keys(cols).filter(k => cols[k] > 0).length;
              return (
                <div key={scopeName} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-xs font-semibold text-slate-700 capitalize">{scopeName}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{count}</p>
                  <p className="text-[10px] text-slate-400">{colCount} collections</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {/* Download Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="backup-download-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Download className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Export Backup</h2>
              <p className="text-[11px] text-slate-500">Download data as JSON file</p>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            {SCOPE_OPTIONS.map(opt => (
              <label key={opt.value} className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-all ${scope === opt.value ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <input type="radio" name="scope" value={opt.value} checked={scope === opt.value} onChange={() => setScope(opt.value)} className="mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-slate-800">{opt.label}</p>
                  <p className="text-[10px] text-slate-500">{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>

          <button onClick={handleDownload} disabled={downloading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-semibold text-sm"
            data-testid="backup-download-btn">
            <Download className="w-4 h-4" />
            {downloading ? 'Downloading...' : 'Download Backup'}
          </button>

          {lastBackup && (
            <div className="mt-3 flex items-center gap-2 text-[10px] text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-200">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Downloaded: {lastBackup.date} ({lastBackup.totalDocs} docs, {lastBackup.collections} collections, scope: {lastBackup.scope})</span>
            </div>
          )}
        </div>

        {/* Restore Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="backup-restore-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Upload className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Import Backup</h2>
              <p className="text-[11px] text-slate-500">Upload a backup file to restore</p>
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 text-[11px] text-emerald-700">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Safe Merge Import</p>
                <p className="mt-0.5">Existing data is <strong>preserved</strong>. Records with matching IDs are updated. New records are inserted. Nothing is deleted.</p>
              </div>
            </div>
          </div>

          <label className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors cursor-pointer font-semibold text-sm"
            data-testid="backup-restore-btn">
            <Upload className="w-4 h-4" />
            {restoring ? 'Importing...' : 'Upload & Import Backup'}
            <input type="file" ref={fileRef} accept=".json" onChange={handleRestore} className="hidden" disabled={restoring} data-testid="backup-file-input" />
          </label>

          {restoreResult && (
            <div className="mt-3 bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-200">
              <div className="flex items-center gap-2 text-[10px] text-emerald-600 font-semibold mb-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Import complete (from {restoreResult.backup_date})</span>
              </div>
              <div className="space-y-0.5 text-[10px] text-slate-600 ml-5 max-h-40 overflow-y-auto">
                {Object.entries(restoreResult.results || {}).map(([col, info]) => (
                  <div key={col}><strong>{col}</strong>: +{info.inserted} new, {info.updated} updated ({info.total} total)</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
